import { type FC } from 'react';
import {
  Loader2Icon,
  CheckCircle2Icon,
  XCircleIcon,
  SearchIcon,
  BookOpenIcon,
  PlusIcon,
  PencilIcon,
  FolderPlusIcon,
  FolderIcon,
  MoveIcon,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '@/lib/utils';

interface KBToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

function parseArgs(args: any, argsText: string): Record<string, any> {
  try {
    return typeof args === 'object' && args ? args : JSON.parse(argsText);
  } catch {
    return {};
  }
}

function parseResult(result?: string): { success?: boolean; error?: string; data: any } {
  if (result === undefined) return { data: {} };
  try {
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    return {
      success: parsed?.success,
      error: parsed?.error,
      data: parsed,
    };
  } catch {
    return { data: {} };
  }
}

const TOOL_CONFIG: Record<string, {
  icon: FC<{ className?: string }>;
  runningLabel: (args: Record<string, any>) => string;
  completedLabel: (args: Record<string, any>) => string;
  resultLabel: (args: Record<string, any>, data: any) => string;
}> = {
  search_kb: {
    icon: SearchIcon,
    runningLabel: (a) => {
      const parts: string[] = [];
      if (a.query) parts.push(`"${a.query}"`);
      if (a.tag) parts.push(`tag: "${a.tag}"`);
      return parts.length > 0 ? `Searching knowledge base for ${parts.join(', ')}...` : 'Browsing knowledge base...';
    },
    completedLabel: (a) => {
      const parts: string[] = [];
      if (a.query) parts.push(`"${a.query}"`);
      if (a.tag) parts.push(`tag: "${a.tag}"`);
      return parts.length > 0 ? `Searched knowledge base for ${parts.join(', ')}` : 'Browsed knowledge base';
    },
    resultLabel: (_a, d) => {
      const count = d?.articles?.length ?? 0;
      return count === 0 ? 'No matching articles found.' : `Found ${count} article${count !== 1 ? 's' : ''}`;
    },
  },
  get_kb_article: {
    icon: BookOpenIcon,
    runningLabel: () => 'Loading knowledge base article...',
    completedLabel: (_a) => 'Knowledge Base',
    resultLabel: (_a, d) => d?.name ? `Loaded "${d.name}"` : 'Article loaded',
  },
  create_kb_article: {
    icon: PlusIcon,
    runningLabel: (a) => a.name ? `Creating article "${a.name}"...` : 'Creating knowledge base article...',
    completedLabel: (a) => a.name ? `Created article: ${a.name}` : 'Created knowledge base article',
    resultLabel: (_a, d) => d?.message || 'Article submitted for approval.',
  },
  update_kb_article: {
    icon: PencilIcon,
    runningLabel: (a) => a.name ? `Updating article "${a.name}"...` : 'Updating knowledge base article...',
    completedLabel: () => 'Update Knowledge Base Article',
    resultLabel: (_a, d) => d?.message || 'Update submitted for approval.',
  },
  create_kb_folder: {
    icon: FolderPlusIcon,
    runningLabel: (a) => a.name ? `Creating folder "${a.name}"...` : 'Creating folder...',
    completedLabel: (a) => a.name ? `Create folder: ${a.name}` : 'Create folder',
    resultLabel: (_a, d) => d?.message || 'Folder created.',
  },
  move_kb_item: {
    icon: MoveIcon,
    runningLabel: () => 'Moving item...',
    completedLabel: () => 'Move Knowledge Base Item',
    resultLabel: (_a, d) => d?.message || 'Item moved.',
  },
  list_kb_folder: {
    icon: FolderIcon,
    runningLabel: () => 'Listing folder contents...',
    completedLabel: () => 'Knowledge Base Folder',
    resultLabel: (_a, d) => {
      const count = d?.items?.length ?? 0;
      return count === 0 ? 'Folder is empty.' : `${count} item${count !== 1 ? 's' : ''}`;
    },
  },
};

const KBToolCallComponent: FC<KBToolCallProps> = ({ toolName, args, argsText, result, status }) => {
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = status?.type === 'complete' && !isError;

  const parsedArgs = parseArgs(args, argsText);
  const { success, error: resultError, data } = parseResult(result);
  const hasError = isError || (result !== undefined && success === false) || !!resultError;

  const config = TOOL_CONFIG[toolName] || TOOL_CONFIG.search_kb;
  const Icon = config.icon;

  return (
    <Card>
      <div className={cn(
        'p-3',
        isRunning ? 'bg-primary/10 border-b border-primary/20' : hasError ? 'bg-destructive/10' : 'bg-muted/50',
      )}>
        {isRunning ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">
              {config.runningLabel(parsedArgs)}
            </p>
          </div>
        ) : hasError ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">
                {config.completedLabel(parsedArgs)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <XCircleIcon className="h-4 w-4 text-destructive flex-shrink-0" />
              <p className="text-sm m-0 text-destructive">
                {resultError || 'Operation failed'}
              </p>
            </div>
          </div>
        ) : isComplete ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <p className="text-sm m-0 text-muted-foreground">
                {config.completedLabel(parsedArgs)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4 text-success flex-shrink-0" />
              <p className="text-sm m-0 text-foreground">
                {config.resultLabel(parsedArgs, data)}
              </p>
            </div>
            {toolName === 'search_kb' && data?.articles?.length > 0 && (
              <div className="ml-6 mt-1 space-y-1">
                {data.articles.map((article: { id: string; name: string; tags?: string[] }) => (
                  <div key={article.id} className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-foreground font-medium">{article.name}</span>
                    {(article.tags || []).slice(0, 3).map((tag: string, i: number) => (
                      <Badge key={i} variant="secondary" className="text-[10px] py-0 px-1.5">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                ))}
              </div>
            )}
            {toolName === 'list_kb_folder' && data?.items?.length > 0 && (
              <div className="ml-6 mt-1 space-y-1">
                {data.items.map((item: { id: string; name: string; item_type: string }) => (
                  <div key={item.id} className="flex items-center gap-2">
                    {item.item_type === 'folder' ? (
                      <FolderIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <BookOpenIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    )}
                    <span className="text-xs text-foreground font-medium">{item.name}</span>
                  </div>
                ))}
              </div>
            )}
            {toolName === 'create_kb_article' && parsedArgs.tags?.length > 0 && (
              <div className="ml-6 mt-1 flex items-center gap-1 flex-wrap">
                {parsedArgs.tags.map((tag: string, i: number) => (
                  <Badge key={i} variant="secondary" className="text-[10px] py-0 px-1.5">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-4 w-4 text-primary animate-spin flex-shrink-0" />
            <p className="text-sm m-0 text-foreground">{config.runningLabel(parsedArgs)}</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export const KBToolCall = KBToolCallComponent as React.ComponentType<any>;
